import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { socket } from "../api";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    phone: "",
    password: "",
    otp: "",
    referralCode: "",
  });

  const [errors, setErrors] = useState({ username: "" });
  const [myReferralCode, setMyReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0); // seconds remaining for resend

  const [searchParams] = useSearchParams();

  // Prefill referral from URL
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setFormData((prev) => ({ ...prev, referralCode: ref }));
    }
  }, [searchParams]);

  // Socket listeners for OTP and signup
  useEffect(() => {
    const onOtpResponse = (data) => {
      if (data.success) {
        toast.success("OTP sent successfully!");
        setMyReferralCode(data.referralCode || "");
        setTimer(30); // start 30s countdown
        // Auto-fill OTP only for demo/debug
        setFormData((prev) => ({ ...prev, otp: data.otp || "" }));
      } else {
        toast.error(data.message || "Failed to send OTP.");
      }
    };

    const onSignupResponse = (data) => {
      setLoading(false);
      if (data.success) {
        toast.success("Signup successful! Redirecting...");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        // Stay on page on failure
        toast.error(data.message || "Signup failed.");
      }
    };

    socket.on("otpResponse", onOtpResponse);
    socket.on("signupResponse", onSignupResponse);

    return () => {
      socket.off("otpResponse", onOtpResponse);
      socket.off("signupResponse", onSignupResponse);
    };
  }, [navigate]);

  // Countdown effect for OTP timer
  useEffect(() => {
    if (timer <= 0) return;

    const intervalId = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timer]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "username") {
      if (/^[A-Za-z ]*$/.test(value)) {
        setFormData((prev) => ({ ...prev, username: value }));
        setErrors((prev) => ({ ...prev, username: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          username: "Username can contain only letters and spaces.",
        }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOTP = () => {
    if (!formData.phone) {
      toast.error("Please enter your phone number.");
      return;
    }

    if (!/^09\d{8}$/.test(formData.phone)) {
      toast.error("Phone number must start with 09 and be 10 digits.");
      return;
    }

    // Emit OTP request; timer will start only when server confirms success
    socket.emit("sendOTP", { phone: formData.phone });
  };

  const handleSignup = (e) => {
    e.preventDefault();

    if (
      !formData.username ||
      !formData.phone ||
      !formData.password ||
      !formData.otp
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    setLoading(true);
    socket.emit("signup", formData);
  };

  return (
    <div className="min-h-screen bg-[#082f52] text-white flex justify-center items-center px-4 py-6 relative overflow-hidden">
      {/* Gradient Shadow Glow (same as login) */}
      <div className="absolute inset-x-1/4 top-24 -inset-y-10">
        <div
          className="w-full h-full mx-auto rotate-180 rounded-3xl opacity-70 blur-3xl"
          style={{
            background:
              "linear-gradient(90deg,#44ff9a -0.55%,#44b0ff 22.86%,#8b44ff 48.36%,#ff6644 73.33%,#ebff70 99.34%)",
          }}
        ></div>
      </div>

      {/* Signup Card */}
      <div className="relative w-full max-w-md bg-[#12385d] rounded-xl shadow-lg p-6 sm:p-8 border border-white/10">
        <h1 className="text-2xl sm:text-3xl font-semibold mb-3 text-center text-white font-serif">
          Create New Account
        </h1>

        <p className="text-gray-300 mb-6 text-center text-sm sm:text-base font-serif space-y-3">
          <span>
            Already Registered?{" "}
            <a
              href="/login"
              className="text-blue-400 hover:text-blue-500 underline"
            >
              Login here
            </a>
          </span>

          {/* Telegram Login Section */}
          <div className="mt-4 bg-[#0d3558] border border-blue-400/30 rounded-lg p-4">
            <p className="text-gray-200 mb-2 text-lg">
              ⚡ Faster & easier access using our Telegram Bot
            </p>

            <p className="text-gray-400 text-sm mb-3">
              ውድ ደንበኞቻችን አዲስ አካውንት ለመክፈት ከታች ያለውን የቴሌግራም ሊንክ ይጠቀሙ ከዛም ምዝገባ
              ሲያጠናቅቁ ተመልሰው ወደ enatgames.com በመምጣት የጌሞቻችን ቤተሰብ መሆን ይችላሉ። ማሳሰቢያ
              የቴሌግራም ቦታችን የሚጠቀሙት አንዴ ለምዝገባ ብቻ ነው ከዛ ሚፈልጉትን ከእናት ጌምስ ሲስተም በቀጥታ
              EnatGames.com ላይ እየገቡ ማግኘት ይችላሉ።
            </p>

            <a
              href={`https://t.me/EnatGamesBot?start=${formData.referralCode || ""}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded-md transition duration-200"
            >
              👉 Continue with Telegram Bot
            </a>
          </div>
        </p>
      </div>

      {/* Toast Popup */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
};

export default Signup;
